// Admin tools (adminTools.js on the server): site health, feature switches, the new-member funnel,
// members going quiet with a check-in email, plans and money, and one member's details. Admin
// text is English only; the check-in email goes out in the member's language.
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

const ago = (date) => {
  if (!date) return 'never';
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};
const day = (date) => (date ? new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—');
const money = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function useLoad(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api(path).then((d) => { setData(d); setError(''); }).catch((err) => setError(err.message)), [path]);
  useEffect(() => { load(); }, [load]);
  return [data, error, load, setData];
}

function Status({ ok, warn, children }) {
  return <span className={`at-status ${ok ? 'ok' : warn ? 'warn' : 'bad'}`}>{ok ? '●' : warn ? '▲' : '■'} {children}</span>;
}

// --- Health ---------------------------------------------------------------------------------

function SyncRow({ name, s, hours = 2 }) {
  const [all, setAll] = useState(false);
  if (!s.connected) return <li><strong>{name}</strong> <span className="at-dim">No members connected yet.</span></li>;
  const fresh = s.lastSyncAt && Date.now() - new Date(s.lastSyncAt) < hours * 3600000;
  return (
    <li>
      <strong>{name}</strong>{' '}
      <Status ok={fresh && !s.failing.length} warn={fresh || s.failing.length < s.connected}>{fresh ? 'Syncing' : 'No sync lately'}</Status>{' '}
      <span className="at-dim">{s.connected} connected · last trades synced {ago(s.lastSyncAt)}
        {s.round && <> · last round {ago(s.round.at)}: {s.round.members} member{s.round.members === 1 ? '' : 's'}, {s.round.failed} failed</>}</span>
      {s.failing.length > 0 && (
        <ul className="at-sub">
          {(all ? s.failing : s.failing.slice(0, 5)).map((f) => (
            <li key={f.id}><MemberLink id={f.id}>{f.name}</MemberLink> <span className="at-dim">{f.email}</span> — {f.error} <span className="at-dim">({ago(f.since)}{f.stopped ? ', stopped trying until they save their key again' : ''})</span></li>
          ))}
          {s.failing.length > 5 && <li><button type="button" className="link-button" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${s.failing.length}`}</button></li>}
        </ul>
      )}
    </li>
  );
}

export function HealthPanel() {
  const [h, error, load] = useLoad('/admin/health');
  const backupOk = h && h.backup.lastGood && Date.now() - new Date(h.backup.lastGood.at) < 36 * 3600000;
  return (
    <section className="admin-panel" id="admin-health">
      <div className="admin-panel-header">
        <div>
          <h2>Health</h2>
          <p className="admin-panel-sub">Is the site running, are broker syncs working, did last night's backup run.</p>
        </div>
        <button type="button" className="secondary small" onClick={load}>Check again</button>
      </div>
      {error && <p className="error">{error}</p>}
      {h && (
        <ul className="at-health">
          <li>
            <strong>Server</strong> <Status ok>Up</Status>{' '}
            <span className="at-dim">running since {ago(h.upSince)} (last deploy or restart) · database answered in {h.dbMs} ms · memory {h.memoryMb} of {h.memoryLimitMb} MB</span>
          </li>
          <SyncRow name="TopstepX" s={h.topstepx} />
          <SyncRow name="Interactive Brokers" s={h.ibkr} />
          <li>
            <strong>Backup</strong> <Status ok={backupOk} warn={false}>{backupOk ? 'OK' : 'Missing'}</Status>{' '}
            <span className="at-dim">{h.backup.lastGood ? `last good backup ${ago(h.backup.lastGood.at)} (${(h.backup.lastGood.bytes / 1048576).toFixed(1)} MB)` : 'no backup yet'}
              {h.backup.last && !h.backup.last.ok && <> · last try failed: {h.backup.last.error}</>}</span>
          </li>
          <li>
            <strong>Problems</strong> <Status ok={!h.problems} warn>{h.problems ? `${h.problems} open` : 'None'}</Status>{' '}
            <span className="at-dim">errors in the last 24 hours, listed under Problems</span>
          </li>
          <li>
            <strong>Services</strong>{' '}
            {[['email', 'Email (Resend)'], ['storage', 'Screenshots and backups (R2)'], ['ai', 'Coach MARK (Anthropic)'], ['discord', 'Discord alerts']].map(([k, label]) => (
              <Status key={k} ok={h.services[k]}>{label}</Status>
            ))}
          </li>
        </ul>
      )}
      <p className="at-note">This checks the site from the inside. UptimeRobot checks it from the outside every 5 minutes (the homepage and /api/founding, which also reads the database) and emails you if visitors can't reach it.</p>
    </section>
  );
}

// --- Feature switches ------------------------------------------------------------------------

export function FeatureSwitches() {
  const [flags, error, , setFlags] = useLoad('/admin/flags');
  const [saving, setSaving] = useState('');
  async function flip(f) {
    const verb = f.on ? 'Close' : 'Open';
    if (!window.confirm(`${verb} ${f.label} ${f.on ? 'to members? Only admins will see it again.' : 'to every member? They see it the next time a page loads.'}`)) return;
    setSaving(f.key);
    try { setFlags(await api(`/admin/flags/${f.key}`, { method: 'PUT', body: { on: !f.on } })); } catch (err) { window.alert(err.message); }
    setSaving('');
  }
  return (
    <section className="admin-panel" id="admin-features">
      <h2>Features</h2>
      <p className="admin-panel-sub">Open an admin-only preview to every member, or close it again. Takes effect within a minute, no deploy needed.</p>
      {error && <p className="error">{error}</p>}
      {flags && (
        <ul className="at-flags">
          {flags.map((f) => (
            <li key={f.key}>
              <div>
                <strong>{f.label}</strong> <Status ok={f.on} warn>{f.on ? 'Open to members' : 'Admins only'}</Status>
                <p className="at-dim">{f.hint}{!f.switched && ` Set by ${f.env} on Render until you flip it here.`}{f.at && ` Changed ${ago(f.at)}.`}</p>
              </div>
              <button type="button" className={f.on ? 'secondary small' : 'small'} disabled={saving === f.key} onClick={() => flip(f)}>{f.on ? 'Close to members' : 'Open to members'}</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- Funnel and quiet members -----------------------------------------------------------------

export function FunnelPanel() {
  const [days, setDays] = useState(90);
  const [f, error] = useLoad(`/admin/funnel?days=${days}`);
  const [open, setOpen] = useState(null);
  return (
    <section className="admin-panel" id="admin-funnel">
      <div className="admin-panel-header">
        <div>
          <h2>New members: where they stop</h2>
          <p className="admin-panel-sub">Of the members who joined, how many reached each step. Click a step to see who stopped just before it.</p>
        </div>
        <div className="inbox-filter">
          {[[30, '30 days'], [90, '90 days'], [0, 'All time']].map(([d, label]) => (
            <button key={d} type="button" className={`secondary small${days === d ? ' active' : ''}`} onClick={() => { setDays(d); setOpen(null); }}>{label}</button>
          ))}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {f && (f.members === 0 ? <p className="empty">No members joined in this period.</p> : (
        <ol className="at-funnel">
          {f.steps.map((s, i) => {
            const pct = s.of ? Math.round((s.count / s.of) * 100) : 0;
            const width = f.members ? (s.count / f.members) * 100 : 0;
            return (
              <li key={s.key}>
                <button type="button" className="at-step" disabled={!s.stopped.length} onClick={() => setOpen(open === s.key ? null : s.key)} aria-expanded={open === s.key}>
                  <span className="at-step-label">{s.label}</span>
                  <span className="at-bar"><span style={{ width: `${Math.max(width, 1)}%` }} /></span>
                  <span className="at-step-num"><strong>{s.count}</strong>{i > 0 && <small> {pct}% of {s.of}</small>}</span>
                </button>
                {s.tooNew > 0 && <p className="at-dim at-indent">{s.tooNew} joined less than a week ago, so it's too early to tell.</p>}
                {open === s.key && (
                  <ul className="at-sub at-indent">
                    {s.stopped.map((m) => <li key={m.id}><MemberLink id={m.id}>{m.name}</MemberLink> <span className="at-dim">{m.email} · joined {day(m.joinedAt)} · last active {ago(m.lastActiveAt)}</span></li>)}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      ))}
    </section>
  );
}

const CHECK_IN = {
  en: {
    subject: 'Checking in from Market Matrix',
    text: (first) => `Hi ${first},\n\nIt's Lou from Market Matrix. I noticed you haven't been on in a little while, so I wanted to check in.\n\nIs anything getting in the way, or did something not work the way you hoped? Just reply to this email. I read every message and I'd love to help.\n\nLou`,
  },
  es: {
    subject: '¿Cómo vas? Saludos de Market Matrix',
    text: (first) => `Hola, ${first}:\n\nSoy Lou, de Market Matrix. Vi que hace unos días no entras, así que quería saber cómo vas.\n\n¿Hay algo que te esté frenando o algo que no funcionó como esperabas? Solo responde a este correo. Leo todos los mensajes y me encantaría ayudarte.\n\nLou`,
  },
};

function CheckIn({ m, onSent, onCancel }) {
  const tpl = CHECK_IN[m.language === 'es' ? 'es' : 'en'];
  const [subject, setSubject] = useState(tpl.subject);
  const [text, setText] = useState(tpl.text((m.name || '').split(' ')[0] || 'there'));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  async function send() {
    setSending(true);
    try {
      await api('/admin/messages/new', { method: 'POST', body: { to: m.email, name: m.name, subject, text } });
      onSent();
    } catch (err) {
      setError(err.message);
      setSending(false);
    }
  }
  return (
    <div className="at-checkin">
      <label>Subject<input value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
      <label>Email ({m.language === 'es' ? 'Spanish' : 'English'}, from hello@)<textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} /></label>
      {error && <p className="error">{error}</p>}
      <div className="at-actions">
        <button type="button" className="small" disabled={sending || !subject.trim() || !text.trim()} onClick={send}>{sending ? 'Sending…' : 'Send'}</button>
        <button type="button" className="secondary small" onClick={onCancel}>Cancel</button>
      </div>
      <p className="at-dim">It goes out from hello@marketmatrixhq.com and their reply lands in the Inbox.</p>
    </div>
  );
}

export function QuietPanel() {
  const [list, error, load] = useLoad('/admin/quiet');
  const [writing, setWriting] = useState(null);
  return (
    <section className="admin-panel" id="admin-quiet">
      <h2>Going quiet</h2>
      <p className="admin-panel-sub">Members who haven't opened the site, added trades or journaled for 7 to 60 days, most recent first.</p>
      {error && <p className="error">{error}</p>}
      {list && (list.length === 0 ? <p className="empty">Nobody has gone quiet. Everyone was active in the last week.</p> : (
        <ul className="at-quiet">
          {list.map((m) => (
            <li key={m.id}>
              <div className="at-quiet-row">
                <span><MemberLink id={m.id}>{m.name}</MemberLink> <span className="at-dim">{m.email} · last active {ago(m.lastActiveAt)} · {m.trades} trades · {m.notes} journal days{m.emailedAt && ` · emailed ${ago(m.emailedAt)}`}</span></span>
                {writing !== m.id && <button type="button" className="secondary small" onClick={() => setWriting(m.id)}>Check in</button>}
              </div>
              {writing === m.id && <CheckIn m={m} onCancel={() => setWriting(null)} onSent={() => { setWriting(null); load(); }} />}
            </li>
          ))}
        </ul>
      ))}
    </section>
  );
}

// --- Plans and money ---------------------------------------------------------------------------

export function MoneyPanel() {
  const [m, error] = useLoad('/admin/money');
  return (
    <section className="admin-panel" id="admin-money">
      <h2>Plans and money</h2>
      <p className="admin-panel-sub">
        {m && !m.paywall ? 'The paywall is off, so nobody pays yet: these numbers fill in once Paddle is live and PAYWALL=1. ' : ''}
        Monthly revenue counts active subscriptions (yearly as a twelfth, founding members at half price).
      </p>
      {error && <p className="error">{error}</p>}
      {m && (
        <>
          <section className="summary admin-summary">
            <div className="stat"><span className="stat-label">Monthly revenue</span><span className="stat-value">{money(m.mrr)}</span><span className="stat-note">{money(m.mrr * 12)} a year</span></div>
            <div className="stat"><span className="stat-label">Pro monthly</span><span className="stat-value">{m.counts.monthly}</span></div>
            <div className="stat"><span className="stat-label">Pro yearly</span><span className="stat-value">{m.counts.yearly}</span></div>
            <div className="stat"><span className="stat-label">On trial</span><span className="stat-value">{m.counts.trial}</span></div>
            <div className="stat"><span className="stat-label">Free</span><span className="stat-value">{m.counts.free}</span></div>
          </section>
          {[['Trials ending in the next 3 days', m.endingTrials, (x) => `ends ${day(x.endsAt)}`], ['Cancelled, ending soon', m.cancelling, (x) => `ends ${day(x.endsAt)}`], ['Payment failed', m.pastDue, () => 'past due']].map(([title, rows, note]) => rows.length > 0 && (
            <div key={title} className="at-list">
              <h3>{title}</h3>
              <ul className="at-sub">{rows.map((x) => <li key={x.id}><MemberLink id={x.id}>{x.name}</MemberLink> <span className="at-dim">{x.email} · {note(x)}</span></li>)}</ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

// --- Pro launch ----------------------------------------------------------------------------------

// The announcement email: preview it in both languages, then send it once to every member.
export function ProLaunchPanel() {
  const [info, error, reload] = useLoad('/admin/pro-launch');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  async function show(lang, founding) {
    setPreview(await api(`/admin/pro-launch/preview?lang=${lang}${founding ? '&founding=1' : ''}`));
  }
  async function send() {
    if (!window.confirm(`Send the Pro launch email to ${info.recipients} member${info.recipients === 1 ? '' : 's'}? Each member gets it once.`)) return;
    setBusy(true);
    setNote('');
    try {
      const r = await api('/admin/pro-launch/send', { method: 'POST' });
      setNote(`Sending to ${r.queued} member${r.queued === 1 ? '' : 's'}, about one a second.`);
      setTimeout(reload, 3000);
    } catch (err) {
      setNote(err.message);
    }
    setBusy(false);
  }
  const ready = info && info.paywall && info.graceUntil;
  return (
    <section className="admin-panel" id="admin-pro-launch">
      <h2>Pro launch</h2>
      <p className="admin-panel-sub">The announcement email: what's Free and what's Pro, that nothing changes until the grace date, and the 50% for founding members. In each member's language, from hello@, once per member.</p>
      {error && <p className="error">{error}</p>}
      {info && (
        <>
          <section className="summary admin-summary">
            <div className="stat"><span className="stat-label">Paywall</span><span className="stat-value at-small">{info.paywall ? 'On' : 'Off'}</span></div>
            <div className="stat"><span className="stat-label">Everything until</span><span className="stat-value at-small">{info.graceUntil ? day(`${info.graceUntil.slice(0, 10)}T12:00:00`) : 'not set'}</span></div>
            <div className="stat"><span className="stat-label">Will get it</span><span className="stat-value at-small">{info.recipients}</span></div>
            <div className="stat"><span className="stat-label">Already sent</span><span className="stat-value at-small">{info.sent}</span></div>
          </section>
          <p className="at-actions">
            <button type="button" className="secondary small" onClick={() => show('en')}>Preview English</button>{' '}
            <button type="button" className="secondary small" onClick={() => show('es')}>Preview Spanish</button>{' '}
            <button type="button" className="secondary small" onClick={() => show('en', true)}>Preview founding version</button>{' '}
            <button type="button" className="small" disabled={!ready || busy || info.sending || !info.recipients} onClick={send}>
              {info.sending ? 'Sending…' : `Send to ${info.recipients} member${info.recipients === 1 ? '' : 's'}`}
            </button>
          </p>
          {!ready && <p className="at-dim">Sending opens once PAYWALL=1 and PAYWALL_GRACE_UNTIL are set on Render.</p>}
          {note && <p className="at-dim">{note}</p>}
        </>
      )}
      {preview && (
        <div className="pp-modal" role="dialog" aria-modal="true" aria-label="Email preview" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div className="pp-modal-box at-email-preview">
            <div className="pp-modal-top">
              <div><h2>Preview</h2><p className="at-dim">Subject: {preview.subject}</p></div>
              <button type="button" className="icon-button" aria-label="Close" onClick={() => setPreview(null)}>&times;</button>
            </div>
            <iframe title="Email preview" srcDoc={preview.html} className="at-email-frame" sandbox="" />
          </div>
        </div>
      )}
    </section>
  );
}

// --- One member ---------------------------------------------------------------------------------

let openMember = () => {};
export function MemberLink({ id, children }) {
  return <button type="button" className="link-button at-member-link" onClick={() => openMember(id)}>{children}</button>;
}

// Readable names for the app pages members spend time on.
const PAGE_NAMES = {
  '/app': 'Trades', '/app/dashboard': 'Dashboard', '/app/home': 'Home', '/app/morning': 'Morning prep', '/app/session': 'Live session',
  '/app/reflection': 'End-of-day reflection', '/app/insights': 'Insights', '/app/accounts': 'Accounts', '/app/goals': 'Goals',
  '/app/notebook': 'Notebook', '/app/playbook': 'Playbook', '/app/settings': 'Settings', '/app/mark': 'Coach MARK', '/app/plans': 'Plans',
  '/app/market-calendar': 'Market events', '/app/compare': 'Compare accounts', '/app/accounts/new': 'New account',
};
const pageName = (path) => PAGE_NAMES[path.replace(/\/$/, '') || '/'] || path;
const days = (n) => <><strong>{n}</strong> day{n === 1 ? '' : 's'}</>;
const share = (n, total) => (total ? `${Math.round((n / total) * 100)}%` : '—');
const duration = (minutes) => (minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`);

// How a member uses Market Matrix: last 30 days of features and pages, plus their habits.
function MemberUsage({ u }) {
  const top = u.features.length ? u.features[0].times : 0;
  const topPage = u.pages.length ? Math.max(1, u.pages[0].minutes) : 1;
  return (
    <>
      <h3>How they use it <span className="at-dim">· last 30 days</span></h3>
      <section className="summary admin-summary">
        <div className="stat"><span className="stat-label">Visits</span><span className="stat-value at-small">{u.visits}</span></div>
        <div className="stat"><span className="stat-label">Time in the app</span><span className="stat-value at-small">{duration(u.minutes)}</span></div>
        <div className="stat"><span className="stat-label">Days active</span><span className="stat-value at-small">{u.activeDays} of 30</span></div>
        <div className="stat"><span className="stat-label">Devices</span><span className="stat-value at-small">{u.devices.length ? u.devices.join(', ') : '—'}</span></div>
      </section>
      <div className="at-cols">
        <div>
          <h3>Most used</h3>
          {u.features.length ? (
            <ul className="at-bars">{u.features.map((f) => (
              <li key={f.name}>
                <span className="at-bar-label">{f.name}</span>
                <span className="at-bar"><i style={{ width: `${(f.times / top) * 100}%` }} /></span>
                <span className="at-dim">{f.times}× · {f.days} day{f.days === 1 ? '' : 's'}</span>
              </li>
            ))}</ul>
          ) : <p className="at-dim">Nothing used in the last 30 days.</p>}
        </div>
        <div>
          <h3>Most time on</h3>
          {u.pages.length ? (
            <ul className="at-bars">{u.pages.map((pg) => (
              <li key={pg.path}>
                <span className="at-bar-label">{pageName(pg.path)}</span>
                <span className="at-bar"><i style={{ width: `${(Math.max(pg.minutes, 0) / topPage) * 100}%` }} /></span>
                <span className="at-dim">{duration(pg.minutes)} · {pg.views} view{pg.views === 1 ? '' : 's'}</span>
              </li>
            ))}</ul>
          ) : <p className="at-dim">No visits in the last 30 days.</p>}
        </div>
      </div>
      <div className="at-cols">
        <div>
          <h3>Routine <span className="at-dim">· all time</span></h3>
          <ul className="at-sub">
            <li>Morning prep on {days(u.habits.morning)}</li>
            <li>Live session on {days(u.habits.session)} · <strong>{u.habits.resets}</strong> reset button{u.habits.resets === 1 ? '' : 's'} used</li>
            <li>End-of-day reflection on {days(u.habits.reflection)}</li>
            <li>Emotions logged on {days(u.habits.emotions)}</li>
          </ul>
        </div>
        <div>
          <h3>How they journal trades</h3>
          <ul className="at-sub">
            <li>Tagged <strong>{share(u.journaling.tagged, u.journaling.trades)}</strong> · graded <strong>{share(u.journaling.graded, u.journaling.trades)}</strong> · notes on <strong>{share(u.journaling.noted, u.journaling.trades)}</strong> of {u.journaling.trades} trades</li>
            <li>Screenshots: {u.journaling.screenshots}</li>
            <li>Notebook pages {u.built.notebook} · goals {u.built.goals} · playbook plays {u.built.plays} · experiments {u.built.experiments}</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function Conn({ name, c }) {
  if (!c) return <li><strong>{name}</strong> <span className="at-dim">not connected</span></li>;
  return (
    <li>
      <strong>{name}</strong> <Status ok={!c.lastError} warn={!c.stopped}>{c.lastError ? (c.stopped ? 'Stopped' : 'Failing') : 'Connected'}</Status>{' '}
      <span className="at-dim">{c.links} account{c.links === 1 ? '' : 's'} linked · last sync {ago(c.lastSyncAt)}{c.lastError && ` · ${c.lastError} (${ago(c.lastErrorAt)})`}</span>
    </li>
  );
}

export function MemberDetail() {
  const [id, setId] = useState(null);
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { openMember = (next) => setId(next); return () => { openMember = () => {}; }; }, []);
  useEffect(() => {
    if (!id) return undefined;
    setM(null);
    setError('');
    api(`/admin/members/${id}`).then(setM).catch((err) => setError(err.message));
    const onKey = (e) => { if (e.key === 'Escape') setId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [id]);
  if (!id) return null;
  const plan = m && (m.plan.plan === 'pro' && m.pro ? `Pro ${m.pro.interval === 'year' ? 'yearly' : 'monthly'}${m.pro.discounted ? ' (founding price)' : ''}${m.pro.cancelAt ? `, ends ${day(m.pro.cancelAt)}` : ''}`
    : m.plan.plan === 'trial' ? `Trial, ${m.plan.trialDaysLeft} day${m.plan.trialDaysLeft === 1 ? '' : 's'} left` : m.plan.plan === 'pro' ? 'Pro' : 'Free');
  return (
    <div className="pp-modal" role="dialog" aria-modal="true" aria-label="Member details" onClick={(e) => { if (e.target === e.currentTarget) setId(null); }}>
      <div className="pp-modal-box at-member">
        <div className="pp-modal-top">
          <div>
            <h2>{m ? m.name : 'Loading…'}</h2>
            {m && <p className="at-dim">{m.email}{m.founding && ` · Founding member #${m.founding}`}</p>}
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={() => setId(null)}>&times;</button>
        </div>
        {error && <p className="error">{error}</p>}
        {m && (
          <>
            <section className="summary admin-summary">
              <div className="stat"><span className="stat-label">Plan</span><span className="stat-value at-small">{plan}</span></div>
              <div className="stat"><span className="stat-label">Joined</span><span className="stat-value at-small">{day(m.joinedAt)}</span></div>
              <div className="stat"><span className="stat-label">Last active</span><span className="stat-value at-small">{ago(m.lastActiveAt)}</span></div>
              <div className="stat"><span className="stat-label">Trades</span><span className="stat-value at-small">{m.trades}</span></div>
              <div className="stat"><span className="stat-label">Journal</span><span className="stat-value at-small">{m.journalWritten} day{m.journalWritten === 1 ? '' : 's'}</span><span className="stat-note">last {m.lastJournal ? day(`${m.lastJournal}T12:00:00`) : 'never'}</span></div>
            </section>
            <div className="at-cols">
              <div>
                <h3>Accounts</h3>
                {m.accounts.length ? (
                  <ul className="at-sub">{m.accounts.map((a) => <li key={a.id}><strong>{a.name}</strong> <span className="at-dim">{a.type}{a.archived ? ' · archived' : ''}{a.attempt ? ' · earlier try' : ''} · {a.trades} trades · last {a.lastTrade ? day(a.lastTrade) : 'none'}</span></li>)}</ul>
                ) : <p className="at-dim">No accounts yet.</p>}
                <h3>Broker sync</h3>
                <ul className="at-sub"><Conn name="TopstepX" c={m.topstepx} /><Conn name="Interactive Brokers" c={m.ibkr} /></ul>
              </div>
              <div>
                <h3>Settings and extras</h3>
                <ul className="at-sub">
                  <li>Language {m.language === 'es' ? 'Spanish' : 'English'}{m.timeZone && ` · ${m.timeZone}`}</li>
                  <li>Sign-in: {m.google ? 'Google' : 'email and password'}{m.twoStep ? ' · two-step on' : ''}{m.discord ? ` · Discord ${m.discord}` : ''}</li>
                  <li>Emails: weekly recap {m.emails.weeklyRecap ? 'on' : 'off'} · morning reminder {m.emails.morningReminder ? 'on' : 'off'}</li>
                  <li>Coach MARK: {m.mark ? `${m.mark.credits} credits, ${m.mark.chats} chats this month` : 'not used'}</li>
                  <li>Share links: {m.shares} · Expenses logged: {m.expenses}</li>
                </ul>
                <h3>Messages</h3>
                {m.messages.length ? (
                  <ul className="at-sub">{m.messages.map((x) => <li key={x.id}>{x.subject} <span className="at-dim">{x.source} · {x.status} · {ago(x.at)}</span></li>)}</ul>
                ) : <p className="at-dim">No messages yet.</p>}
              </div>
            </div>
            {m.usage && <MemberUsage u={m.usage} />}
          </>
        )}
      </div>
    </div>
  );
}
